import Navigo, { BeforeHook, Match } from 'navigo';
import { makeAutoObservable } from 'mobx';

import { getFeatureFlagValue } from '@bowery-valuation/feature-flagger-client';

import { RootStore } from 'stores/RootStore';
import { featureFlags } from 'utils/config';

export type UnderwritingRouteName =
  // | 'property'
  | 'propertySummary'
  | 'residentialRentRoll'
  | 'residentialRentComps'
  | 'residentialProjectedRentRoll'
  | 'commercialRentRoll'
  | 'commercialRentComps'
  | 'commercialReimbursements'
  | 'commercialProjectedRentRoll'
  | 'expenseHistory'
  | 'expenseComps'
  | 'expenseForecasts'
  | 'taxes'
  | 'proForma'
  | 'capRateConclusion'
  | 'otherIncome'
  | 'financingTerms'
  | 'settingsAndExport';

export const PropertyUnderwritingPages: UnderwritingRouteName[] = [
  // 'property',
  'propertySummary',
  'residentialRentRoll',
  'residentialRentComps',
  'residentialProjectedRentRoll',
  'commercialRentRoll',
  'commercialRentComps',
  'commercialReimbursements',
  'commercialProjectedRentRoll',
  'otherIncome',
  'taxes',
  'expenseHistory',
  'expenseComps',
  'expenseForecasts',
  'proForma',
  'capRateConclusion',
  'financingTerms',
  'settingsAndExport',
];

export type RouteName =
  | 'register'
  | 'login'
  | 'loginContact'
  | 'registerContact'
  | 'loginTerms'
  | 'registerTerms'
  | 'resetPasswordEmail'
  | 'resetPassword'
  | 'diligence'
  | 'requestAccess'
  | 'createUser'
  | 'editUsers'
  | 'editUser'
  | 'account'
  | 'completeProfile'
  | 'userProfile'
  | 'confirmEmailSent'
  | 'confirmEmailCheckToken'
  | 'jobs'
  | 'underwritingDashboard'
  | 'compDatabase'
  | 'notifications'
  | UnderwritingRouteName;

const underwritingRouteToUrlMap: { [key in UnderwritingRouteName]: string } = {
  // property: '/property/:id/property-summary',
  propertySummary: '/property/:id/property-summary',
  residentialRentRoll: '/property/:id/residential/rent-roll',
  residentialRentComps: '/property/:id/residential/rent-comps',
  residentialProjectedRentRoll: '/property/:id/residential/projected-rent-roll',
  commercialRentRoll: '/property/:id/commercial/rent-roll',
  commercialRentComps: '/property/:id/commercial/rent-comps',
  commercialReimbursements: '/property/:id/commercial/reimbursements',
  commercialProjectedRentRoll: '/property/:id/commercial/projected-rent-roll',
  expenseHistory: '/property/:id/expense-history',
  expenseComps: '/property/:id/expense-comps',
  expenseForecasts: '/property/:id/expense-forecasts',
  taxes: '/property/:id/taxes',
  proForma: '/property/:id/pro-forma',
  capRateConclusion: '/property/:id/cap-rate-conclusion',
  otherIncome: '/property/:id/other-income',
  financingTerms: '/property/:id/financing-terms',
  settingsAndExport: '/property/:id/settings-and-export',
};

const RouteToUrlMap: { [key in RouteName]: string } = {
  register: '/register',
  login: '/login',
  loginContact: '/login/contact',
  registerContact: '/register/contact',
  loginTerms: '/login/terms',
  registerTerms: '/register/terms',
  resetPasswordEmail: '/resetPassword',
  resetPassword: '/resetPassword/',
  diligence: '/diligence/',
  requestAccess: '/requestAccess/',
  createUser: '/admin/users/create',
  editUsers: '/admin/users/edit',
  editUser: '/admin/users/edit/:username',
  account: '/settings/account',
  completeProfile: '/settings/completeProfile',
  userProfile: '/settings/profile',
  confirmEmailSent: '/settings/email/confirm',
  confirmEmailCheckToken: '/settings/email/confirm/',
  jobs: '/jobs',
  underwritingDashboard: '/underwriting',
  compDatabase: '/compDatabase',
  notifications: '/settings/notifications',
  ...underwritingRouteToUrlMap,
};

export const homeUrlStorageKey = 'homeUrl';

export type IRouteStore = {
  route?: RouteName;
  navigateByName: (route: RouteName, data?: Object) => void;
  goToRedirectUrl: () => void;
  goHome: () => void;
};

/*
 * Makes current route and route name observable,
 * and enforces type on route name.
 */
export class RouteStore implements IRouteStore {
  private readonly userRegistration: boolean;
  private homeUrl?: string;
  private router: Navigo;
  private match?: Match;

  private redirectUrl?: string;
  private setRedirectUrl = (currentUrl: string) => {
    const notLoggedInRoutes: RouteName[] = [
      'register',
      'login',
      'loginContact',
      'registerContact',
      'loginTerms',
      'registerTerms',
      'resetPasswordEmail',
      'resetPassword',
      'confirmEmailSent',
      'confirmEmailCheckToken',
    ];
    if (!notLoggedInRoutes.find((route) => currentUrl.includes(RouteToUrlMap[route]))) {
      this.redirectUrl = currentUrl;
    }
  };

  constructor(private readonly rootStore: RootStore) {
    this.homeUrl = window.localStorage.getItem(homeUrlStorageKey) || undefined;
    this.router = new Navigo('/');
    makeAutoObservable(this, {}, { autoBind: true });

    this.userRegistration = getFeatureFlagValue(featureFlags.userRegistration);

    this.router.hooks({
      before: (done, match) => {
        if (match.route.name === 'diligence') {
          // Last diligence page visited will become default page
          this.homeUrl = match.url;
          window.localStorage.setItem(homeUrlStorageKey, match.url);
        }
        if (match.route.name === 'confirmEmailCheckToken') {
          this.homeUrl = 'login';
          window.localStorage.setItem(homeUrlStorageKey, 'login');
        }
        if (match.route.name === 'login') {
          this.homeUrl = undefined;
          window.localStorage.removeItem(homeUrlStorageKey);
        }
        done();
      },
      after: (match) => {
        this.setMatch(match);
      },
    });

    this.router.notFound(() => {
      console.warn('Route not found');
      this.goHome();
    });
  }
  goToRedirectUrl = () => {
    if (this.redirectUrl) {
      this.router.navigate(this.redirectUrl);
      this.redirectUrl = undefined;
    } else {
      this.goHome();
    }
  };

  setMatch(match: Match) {
    this.match = match;
  }

  navigateByName = (route: RouteName | string, data?: Object) => {
    const result = this.router.navigateByName(route as string, data);

    if (!result) throw new Error('Route not found');
  };

  get route(): RouteName | UnderwritingRouteName | undefined {
    return this.match ? (this.match.route.name as RouteName) : undefined;
  }

  init(initialUrl?: string) {
    if (initialUrl) {
      this.setRedirectUrl(initialUrl);
    }

    const rootStore = this.rootStore;

    this.router.on({
      '/': {
        uses: this.goHome,
      },
      register: {
        uses: (match: Match) => rootStore.registerUserStore.activate(match),
      },
      login: {
        uses: (match: Match) => rootStore.loginStore.activate(match),
      },
      'login/contact': {
        as: 'loginContact',
        uses: () => {},
      },
      'register/contact': {
        as: 'registerContact',
        uses: () => {},
      },
      'login/terms': {
        as: 'loginTerms',
        uses: () => {},
      },
      'register/terms': {
        as: 'registerTerms',
        uses: () => {},
      },
      resetPassword: {
        as: 'resetPasswordEmail',
        uses: (match: Match) => rootStore.resetPasswordEmailStore?.activate(match),
      },
      'resetPassword/:token': {
        as: 'resetPassword',
        uses: (match: Match) => rootStore.resetPasswordStore?.activate(match),
      },
      'settings/account': {
        as: 'account',
        uses: (match: Match) => rootStore.changePasswordStore?.activate(match),
        hooks: { before: this.getBeforeHook({ session: true, profile: true }) },
      },
      'settings/notifications': {
        as: 'notifications',
        uses: (match: Match) => rootStore.notificationSettingsStore?.activate(match),
        hooks: { before: this.getBeforeHook({ session: true, profile: true }) },
      },
      'settings/completeProfile': {
        as: 'completeProfile',
        uses: (match: Match) => rootStore.completeProfileStore?.activate(match),
        hooks: { before: this.getBeforeHook({ session: true }) },
      },
      'settings/profile': {
        as: 'userProfile',
        uses: (match: Match) => rootStore.userProfileStore?.activate(match),
        hooks: { before: this.getBeforeHook({ session: true }) },
      },
      'settings/email/confirm': {
        as: 'confirmEmailSent',
        uses: (match: Match) => rootStore.confirmEmailStore?.activateEmailSent(match),
      },
      'settings/email/confirm/:token': {
        as: 'confirmEmailCheckToken',
        uses: (match: Match) => rootStore.confirmEmailStore?.activateCheckToken(match),
      },
      'admin/users/create': {
        as: 'createUser',
        uses: (match: Match) => rootStore.createUserStore?.activate(match),
        hooks: { before: this.getBeforeHook({ session: true, admin: true }) },
      },
      'admin/users/edit': {
        as: 'editUsers',
        uses: (match: Match) => rootStore.userListStore?.activate(match),
        hooks: { before: this.getBeforeHook({ session: true, admin: true }) },
      },
      'admin/users/edit/:username': {
        as: 'editUser',
        uses: (match: Match) => rootStore.editUserStore?.activate(match),
        hooks: { before: this.getBeforeHook({ session: true, admin: true }) },
      },
      'diligence/:token': {
        as: 'diligence',
        uses: (match: Match) => rootStore.diligencePageStore.activate(match),
        hooks: {
          before: this.userRegistration ? this.getBeforeHook({ session: true, profile: true }) : undefined,
        },
      },
      'requestAccess/:token': {
        as: 'requestAccess',
        uses: (match: Match) => rootStore.requestAccessPageStore.activate(match),
        hooks: {
          before: this.userRegistration ? this.getBeforeHook({ session: true, profile: true }) : undefined,
        },
      },
      jobs: {
        as: 'jobs',
        uses: (match: Match) => rootStore.jobsViewStore?.activate(match),
        hooks: {
          before: this.getBeforeHook({
            session: true,
            profile: true,
            canActivate: () => this.rootStore.jobsViewStore?.canActivate(),
          }),
        },
      },
      underwriting: {
        as: 'underwritingDashboard',
        uses: () => {},
        hooks: {
          before: this.getBeforeHook({
            session: true,
            profile: true,
            featureFlag: featureFlags.showNewClientTech,
          }),
        },
      },
      compDatabase: {
        as: 'compDatabase',
        uses: () => {},
        hooks: {
          before: this.getBeforeHook({
            session: true,
            profile: true,
            featureFlag: featureFlags.showNewClientTech,
          }),
        },
      },

      ...Object.entries(underwritingRouteToUrlMap).reduce(
        (acc, [as, path]) => ({
          ...acc,
          [path]: {
            as,
            hooks: {
              before: this.getBeforeHook({
                session: true,
                profile: true,
                featureFlag: featureFlags.showNewClientTech,
              }),
            },
            uses: (match: Match) => {
              rootStore.underwritingPageStore.activate(match);
            },
          },
        }),
        {}
      ),
    });
    this.router.resolve();
  }

  getBeforeHook =
    ({
      session,
      admin,
      profile,
      featureFlag = undefined,
      canActivate = undefined,
    }: {
      session?: boolean;
      admin?: boolean;
      profile?: boolean;
      featureFlag?: string;
      canActivate?: () => Promise<boolean>;
    } = {}): BeforeHook =>
    async (done, match) => {
      const rootStore = this.rootStore;

      if (!rootStore.authStore.isLoggedIn) {
        if (session) {
          console.warn('Page requires session');
          this.navigateByName('login');
          done(false);
        } else {
          done();
        }
        return;
      }

      if (profile && !rootStore.authStore.isInternal && !rootStore.authStore.currentUser?.profileComplete) {
        console.warn('Page requires complete user profile');
        this.navigateByName('completeProfile');
        done(false);
        return;
      }

      if (admin && !rootStore.authStore.isAdmin) {
        console.warn('Page requires admin user');
        this.goHome();
        done(false);
        return;
      }

      if (featureFlag) {
        await rootStore.authStore.ensureFeatureFlagsLoaded();

        const featureFlagValue = rootStore.authStore.getFeatureFlagValueForUser(featureFlag);
        if (!featureFlagValue) {
          console.warn('Feature is not enabled');
          this.goHome();
          done(false);
          return;
        }
      }

      if (canActivate) {
        const canAccessRoute = await canActivate();
        if (!canAccessRoute) {
          console.warn('Not allowed to access this page');
          this.navigateByName('userProfile');
          done(false);
          return;
        }
      }

      done();
    };

  goHome = () => {
    if (this.homeUrl) {
      this.router.navigate(this.homeUrl);
    } else {
      this.navigateByName('jobs');
    }
  };
}
